import { mapGetters, mapActions } from "vuex"
import analyticsMixin from '../analytics.mixin'

export default {
  components: {
    CostInfo: () => import('@/components/CostInfo.vue'),
  },

  mixins: [analyticsMixin],

  data() {
    return {
      formData: [],
      currentStep: 1,
      jurisdiction: '',
      childrenOptions: '',
      currentCounty: '',
      currentState: '',
      currentType: '',
      collaborate: true,
      isEditable: false,
    };
  },

  watch: {
    currentStep(newVal) {
      if(newVal < 3) {
        this.currentCounty = ''
      }

      if(newVal === 1) {
        this.currentState = ''
        this.currentType = ''
        this.childrenOptions = ''
      }
    }
  },

  computed: {
    ...mapGetters('workflowTypes', ['typeByState', 'types']),

    ...mapGetters("profile", ["userInfo", "myStates", "getPayments"]),

    ...mapGetters("prices", ["getPriceByLevel"]),

    ...mapGetters("workflowPublished", {
      forms: "publishedWorkflows",
      getPublishedWorkflowByState: "getPublishedWorkflowByState"
    }),

    typesByStateAndForm() {
      return (currentState) => {
        const workflowTypesByState = this.forms?.filter(f => f.state === currentState).map(f => f.type)
        return workflowTypesByState
      }
    },

    unilateral() {
      return this.getPriceByLevel('unilateral','WithoutSpouse')
    },

    collaborative() {
      return this.getPriceByLevel('collaborative','WithSpouse')
    },

    formType() {
      return this.collaborate ? 'collaborative' : 'unilateral'
    },

    countiesByStateAndKids() {
      const counties = []
      this.countiesByState
        .filter(fd => fd?.childrenOptions && (fd.childrenOptions === this.childrenOptions || this.childrenOptions === ''))
        .forEach(fd => {
          fd.counties.forEach(c => {
            counties.push(c)
          })
        })
      return counties.sort().flat()
    },

    needsChildren() {
      return this.formData.filter(fd => fd.type === this.currentType && fd.state === this.currentState && !['both','N/A'].includes(fd.childrenOptions)).length > 0 || false
    },

    hasCounties() {
      return this.countiesByState.length > 0
    },

    hasType() {
      return this.formData.filter(fd => fd.state === this.currentState && fd.type === this.currentType).length > 0
    },

    unilateralPayments() {
      return this.types.filter(type => type.title === this.currentType)?.[0]?.costs?.unilateral
    },

    unilateralCost() {
      if(this.unilateralPayments) {
        const total = parseInt(this.unilateralPayments?.paywall1) + parseInt(this.unilateralPayments?.paywall2)
        return total
      }
      return this.unilateral.price
    },

    replacements() {
      return (paywallType) => {
        return [
          { 'p1': this[paywallType]?.paywall1 },
          { 'p2': this[paywallType]?.paywall2 },
          { 'total': paywallType === 'collaborativePayments' ? this.collaborativeCost : this.unilateralCost }
        ]
      }
    },
    unilateralItems() {
      if(this.unilateralPayments) {
        return this.unilateral.items.replace('[p1]',this.unilateralPayments?.paywall1).replace('[p2]',this.unilateralPayments?.paywall2)
      }
      return this.unilateral?.items
    },

    collaborativeItems() {
      if(this.collaborativePayments) {
        return this.collaborative.items.replace('[p1]',this.collaborativePayments?.paywall1).replace('[p2]',this.collaborativePayments?.paywall2)
      }
      return this.collaborative?.items
    },

    collaborativePayments() {
      return this.types.filter(type => type.title === this.currentType)?.[0]?.costs?.collaborative
    },

    collaborativeCost() {
      if(this.collaborativePayments) {
        const total = parseInt(this.collaborativePayments?.paywall1) + parseInt(this.collaborativePayments?.paywall2)
        return total
      }
      return this.collaborative.price
    },

    countiesByState() {
      return this.formData.filter(fd => fd?.useCounties && fd.state === this.currentState && fd?.counties?.length > 0) || []
    },

    getStates() {
      const states = this.myStates.map(r => ({ ...r, notPublished: true }))

      const rval = [...this.forms, ...states]

      return rval.filter(r => r.state && r.type).map(s => s.state).sort()
    },
  },

  beforeMount() {
    this.setPublishedWorkflows()
  },

  beforeDestroy() {
    this.$store.dispatch("workflowPublished/resetWorkflowPublished");
  },

  async created() {
    await this.establishUserAuth()
    if(this.userInfo.forms.length === 1) {
      void this.$router.push(`/legal-form-guide/divorce-forms/${this.userInfo.forms[0].workflowsUserId}`).catch(() => {});
    }
  },

  async mounted() {
    if(this.getPayments && Object.keys(this.getPayments).length > 0) {
      await this.toggleLoadingOverlayStatus(true)
      this.setOverlayMessage('Please wait while we build your questionnaire.')
      const parentId = Object.keys(this.getPayments)[0]
      await this.loadForm({ parentId })
      const formId = await this.saveForm(false)
      void this.$router.push(`/legal-form-guide/divorce-forms/${formId}`)
      await this.toggleLoadingOverlayStatus(true)
    }
    await this.getMyStates(this.userInfo.email)
    await this.setPrices()
    this.setOverlayMessage()

    if(this.types.length === 0) {
      this.dataLoading = true
      await this.setTypesAction()
      this.dataLoading = false
    }
  },

  methods: {
    ...mapActions('workflowTypes', ['setTypesAction']),      

    ...mapActions(["toggleLoadingOverlayStatus", "setOverlayMessage"]),

    ...mapActions("workflowPublished", ["setPublishedWorkflows", "getRawFormDataByStateAndType", "loadForm", "saveForm"]),

    ...mapActions("profile", ["getMyStates", "establishUserAuth"]),

    ...mapActions("alert", ["setAlert"]),

    ...mapActions("prices", ["setPrices"]),

    countySelected() {
      const increment = this.collaborate ? 1 : 2
      this.handleFindingForm(increment)
    },

    changeStep(evt) {
      this.currentStep = parseInt(evt)
      this.pauseVideo()
    },

    handleChildrenOptions() {
      const increment = this.countiesByStateAndKids.length > 0 ? 1 : (this.collaborate ? 2 : 3)
      this.handleFindingForm(increment)
    },

    collaborateClick(collaborate) {
      this.collaborate = collaborate
      let increment = collaborate ? 5 : 6
      increment = this.needsChildren ? (this.currentStep + 1) : increment
      increment = !this.hasCounties && !this.needsChildren ? 6 : increment
      this.currentStep = increment
    },

    handleFindingForm(increment = 1) {
      this.myForm = this.findMyForm()
      if(this.myForm) {
        this.jurisdiction = this.myForm?.jurisdiction
        this.currentStep += increment
      } else {
        this.setAlert({ type: 'error', message: `Sorry, could not find a ${this.currentState} form ${this.childrenOptions} children for ${this.currentCounty || 'any'} county`})
      }
    },

    pauseVideo() {
      if(this.$refs?.tutorialVideo?.player)
        this.$refs.tutorialVideo.player.pauseVideo()
    },

    findMyForm() {
      let filteredForms = this.formData.filter(f => f.state === this.currentState)
      if(this.childrenOptions?.length > 0) {
        filteredForms = filteredForms.filter(f => f.childrenOptions === this.childrenOptions)
      }

      if(this.currentCounty?.length > 0) {
        filteredForms = filteredForms.filter(f => f.counties.indexOf(this.currentCounty) > -1)
      }

      if(this.currentType?.length > 0) {
        filteredForms = filteredForms.filter(f => f.type === this.currentType)
      }
      
      return filteredForms[0]
    },

    async redirectToState() {
      this.$store.state.isCollaborate = this.collaborate
      const relatedForm = this.findMyForm()
      localStorage.setItem('collaborate', this.collaborate)
      void this.$router.push(`/pay-for-form/${relatedForm.id}/${this.userInfo.email}/${this.formType}/1`).catch(() => {});
      return
    },

    nextClick() {
      this.currentStep += 1
      this.pauseVideo()
    },

    async stateSelected(state, type) {
      this.formData = await this.getRawFormDataByStateAndType({ formName: 'workflows-published', state, type })
      if(this.formData.length === 1) {
        this.jurisdiction = this.formData[0].jurisdiction
        if(this.countiesByState.length === 0 && !this.jurisdiction) {
          this.redirectToState()
        } else {
          this.isEditable = false
        }
      } else {
        this.isEditable = true
      }
    },

    async typeSelected(type) {
      if(!type) return
      await this.stateSelected(this.currentState, type)
      if(!this.hasType) {
        this.setAlert({ type: 'error', message: `Sorry, could not find a ${this.currentType} form for ${this.currentState}`})
        return
      }
      this.currentStep += 1
    }
  },
};